import { Expense, TripFolder } from "@finway-group/shared/lib/models"
import { Button, Modal, Tooltip } from "antd"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { AttachIcon } from "~Features/pages/expenses/expenseDetails/icons/attach.icon"
import { useCompany } from "~Shared/hooks/company.hooks"
import { ApprovalProcessService, NotificationService } from "~Shared/services"
import { NotificationTypeEnum } from "~Shared/services/notification.service"
import { attachExpenses } from "~Shared/store/actions/expense/expenseActions"
import { getNextApproverId } from "~Shared/utils/approvalProcess.utils"
import { isExpenseAttachableToFolder } from "~Shared/utils/expense.utils"
import { FOLDER_CHILDREN_LIMIT } from "~Shared/utils/folder.utils"
import { useStateIfMounted } from "~Shared/utils/hooks/useStateIfMounted"
import { getPopupAnchor } from "~Shared/utils/popup.utils"
import { AttachExpenseTable } from "./attachExpense.table"

interface AttachExpenseButtonProps {
    folder: TripFolder
    subExpenseCount: number
}
export const AttachExpenseButton = ({ folder, subExpenseCount }: AttachExpenseButtonProps) => {
    const { t } = useTranslation()
    const [isShowing, setIsShowing] = useStateIfMounted(false)
    const [disableSelection, setDisableSelection] = useStateIfMounted(false)
    const dispatch = useDispatch()
    const [selectedExpenses, setSelectedExpenses] = useStateIfMounted<Array<Expense>>([])
    const okDisabled = selectedExpenses.length === 0
    const company = useCompany()
    const [isLoading, setIsLoading] = useStateIfMounted(false)

    useEffect(() => {
        let mounted = true

        if (mounted) {
            setDisableSelection(selectedExpenses.length + subExpenseCount >= FOLDER_CHILDREN_LIMIT)
        }

        return () => {
            mounted = false
        }
    }, [setDisableSelection, selectedExpenses.length, subExpenseCount])

    const onAttachExpenses = async () => {
        const subExpenseIds = selectedExpenses.map((subExpense) => subExpense.id)
        const grossDiff = selectedExpenses.reduce((acc, expense) => acc + expense.totalGrossPrice, 0)
        const isFolderAmountChangeApproved = await ApprovalProcessService.confirmFolderAmountChange(grossDiff, folder, company)
        if (!isFolderAmountChangeApproved) {
            return
        }
        const folderNextApprover = getNextApproverId(folder.approvalProcesses)
        for (const subExpense of selectedExpenses) {
            if (!isExpenseAttachableToFolder({ approvalProcesses: folder.approvalProcesses, expense: subExpense })) {
                NotificationService.send(
                    NotificationTypeEnum.WARNING,
                    t("Warning"),
                    t("notification:folder_requester_is_same_as_expense_next_approver", { expenseNumber: subExpense.expenseNumber }),
                    10,
                    `warning-${subExpense.id}`, // unique key to show all warnings
                )
                subExpenseIds.splice(subExpenseIds.indexOf(subExpense.id), 1)
            }
        }
        if (subExpenseIds.length === 0) {
            setSelectedExpenses([])
            setIsShowing(false)
            return
        }

        setIsLoading(true)
        try {
            await attachExpenses(folder.id, subExpenseIds)(dispatch)
            NotificationService.send(
                NotificationTypeEnum.SUCCESS,
                t(`notification:request.attached.title`),
                t(`notification:request.attached.message`, { folderName: folder?.folderName }),
            )
            setSelectedExpenses([])
            setIsShowing(false)
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <>
            {isShowing && (
                <Modal
                    open={isShowing}
                    title={t("action:request.folder.attach_expense")}
                    onCancel={() => setIsShowing(false)}
                    onOk={onAttachExpenses}
                    maskClosable={false}
                    className="ant-modal--large"
                    footer={[
                        <div key="1" className="flex gap-12 justify-end">
                            <Button key="cancel" onClick={() => setIsShowing(false)}>
                                {t("action:cancel")}
                            </Button>
                            <Tooltip title={t("tooltips:folder.attach_empty")} trigger={okDisabled ? ["hover"] : []} key="okbutton" getTooltipContainer={getPopupAnchor()}>
                                <Button
                                    key="submit"
                                    type="primary"
                                    onClick={onAttachExpenses}
                                    disabled={okDisabled || selectedExpenses.length + subExpenseCount > FOLDER_CHILDREN_LIMIT}
                                    loading={isLoading}
                                >
                                    {t("action:request.folder.attach_expense")}
                                </Button>
                            </Tooltip>
                        </div>,
                    ]}
                >
                    <AttachExpenseTable selectedExpenses={selectedExpenses} setSelectedExpenses={setSelectedExpenses} disableSelection={disableSelection} />
                </Modal>
            )}
            <Button className="m-5" onClick={() => setIsShowing(true)} disabled={disableSelection}>
                <AttachIcon className="mr-5" /> {t("action:request.folder.attach")}
            </Button>
        </>
    )
}
